import { Observable } from 'rxjs';
import { map } from "rxjs/operators";
import { EventEmitter, inject, Injectable, OnInit, Output } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ChangePasswordViewModel } from "../system/admin-panel/Users/users-dialog-base/users-dialog-base.component";
import { SyncService } from "./sync.service";
import { ObjectGroupViewModel } from "../system/admin-panel/ObjectGroupViewModel";
import { ContainerViewModel } from "../system/admin-panel/ContainerViewModel";

import { IManagableObject } from "../system/admin-panel/IManagableObject";
import { ProfileStoreService } from "../system/mobile-system/services/profile-store-service";

export enum AccessRights {
  VIEW_CURPOS = 0,
  VIEW_HISTORY = 4,
  EDIT = 8,
  EDIT_TRACKERS_IMEI = 9,
  VIEW_TRACKER_PACKET = 10,
  EDIT_TRACKERS = 11,
  EDITUSERS = 16,
  EDIT_ALL_USERS = 24
}

export enum InterfacePermissions {
  USERS = -4,
  DIAGNOSTICS = -3,
  DATAINFO = -2,
  EDIT = -1,

  ANALYTICS = 0,
  TRACKING = 1,
  HISTORY = 2,
  REPORTS = 3,
  DRIVERS = 4,
  SERVICE = 6,
  DISPATCHER = 8
}

export class ApplicationUserStatisticsView implements IManagableObject {

  Id: number;
  Name: string;
  IsChecked: boolean;
  CanSelect?: boolean;
  Email: string;
  CompanyId: number;
  Acl: number;
  InterfacePermissions: number;
  ObjectsCount: number;
  GeozonesCount: number;
  IsDisabled: boolean;
  IsEmailConfirmed: boolean;
  LoginDate: number = 0;

  init(data?: any): void {
    if (data) {
      this.Id = data["id"];
      this.Email = data["email"];
      this.Name = data["name"];
      this.CompanyId = data["companyId"];
      this.Acl = data["acl"];
      this.InterfacePermissions = data["interfacePermissions"];
      this.ObjectsCount = data["objectsCount"];
      this.GeozonesCount = data["geozonesCount"];
      this.LoginDate = data["loginDate"];
      this.IsEmailConfirmed = data["isEmailConfirmed"];
      this.CanSelect = data["canSelect"];
    }
  }


  static fromJS(data: any): ApplicationUserStatisticsView {
    data = typeof data === 'object' ? data : {};
    let result = new ApplicationUserStatisticsView();
    result.init(data);
    return result;

  }

  public CanDo(acl: AccessRights): boolean {
    return (this.Acl & (1 << acl)) != 0;
  }

  public InterfaceIsPermitted(acl: InterfacePermissions): boolean {
    if (acl < 0) {
      switch (acl) {
        case InterfacePermissions.DATAINFO:
        case InterfacePermissions.DIAGNOSTICS:
          return this.CanDo(AccessRights.VIEW_TRACKER_PACKET);
        case InterfacePermissions.USERS:
          return this.CanDo(AccessRights.EDITUSERS) || this.CanDo(AccessRights.EDIT_ALL_USERS);
      }
      return this.CanDo(AccessRights.EDIT);
    }


    if ((this.InterfacePermissions & (1 << acl)) == 0)
      return false;

    switch (acl) {
      case InterfacePermissions.ANALYTICS:
      case InterfacePermissions.HISTORY:
      case InterfacePermissions.REPORTS:
        return this.CanDo(AccessRights.VIEW_HISTORY);
      case InterfacePermissions.DRIVERS:
      case InterfacePermissions.SERVICE:
        return this.CanDo(AccessRights.VIEW_HISTORY);
    }
    return true;
  }

  public RoleDisplayName(): string {
    if (this.CanDo(AccessRights.EDIT_ALL_USERS))
      return "Administrator";
    if (this.CanDo(AccessRights.EDITUSERS))
      return "Integrator";
    if (this.CanDo(AccessRights.EDIT))
      return "User";

    if (this.CanDo(AccessRights.VIEW_HISTORY))
      return "Demonstration";

    return "Observation";
  }
}
export class UserViewModel implements IManagableObject {
  Id: number = 0
  CompanyId: number;
  Name: string = "";
  ObjectsCount: number = 0;
  GeozonesCount: number = 0;

  IsChecked: boolean;
  CanSelect?: boolean;
  Email: string = "";
  IsDisabled: boolean = false;
  LoginDate: number = 0;
  Acl: number = 65535;
  InterfacePermissions: number = 0x7FFFFFFF;
  IsEmailConfirmed: boolean = false;

  UserName: string = "";
  PhoneNumber: string = "";
  TimeZone: string = "";
  Language: string = "";
  Geocoder: string = "";
  Flags: string = "";

  FromLimit: number = 0;
  TillLimit: number = 0;
  CreatedAt: Date;
  ObjectGroupLinks: { ObjectGroupId: number; ACL: number }[] = [];
  ContainerLinks: { ContainerId: number; ACL: number }[] = [];

  //TODO:temp resolve (take it out to UpdateModel when we will refactore client side)
  Password: string = "";
  PasswordConfirmation: string = "";

  private normalizeObjectGroupLinks(links: any[] | undefined): { ObjectGroupId: number; ACL: number }[] {
    if (!Array.isArray(links)) return [];

    return links.map(link => ({
      ObjectGroupId: link?.ObjectGroupId ?? link?.objectGroupId ?? 0,
      ACL: link?.ACL ?? link?.acl ?? 0
    }));
  }

  private normalizeContainerLinks(links: any[] | undefined): { ContainerId: number; ACL: number }[] {
    if (!Array.isArray(links)) return [];

    return links.map(link => ({
      ContainerId: link?.ContainerId ?? link?.containerId ?? 0,
      ACL: link?.ACL ?? link?.acl ?? 0
    }));
  }

  init(data?: any): void {
    if (data) {
      this.Id = data["id"];
      this.Email = data["email"];
      this.IsDisabled = data["isDisabled"];
      this.Name = data["name"];

      this.CompanyId = data["companyId"];
      this.PhoneNumber = data["phoneNumber"];
      this.TimeZone = data["timeZone"];
      this.Language = data["language"];
      this.Geocoder = data["geocoder"];
      this.Flags = data["flags"];
      this.Acl = data["acl"];
      this.InterfacePermissions = data["interfacePermissions"];
      this.CanSelect = data["canSelect"];

      //this.ParentUserName = data["parentUserName"];

      this.LoginDate = data["loginDate"];
      this.IsEmailConfirmed = data["isEmailConfirmed"];

      this.ObjectsCount = data["objectsCount"];
      this.GeozonesCount = data["geozonesCount"];

      this.FromLimit = data["fromLimit"];
      this.TillLimit = data["tillLimit"];
      this.CreatedAt = new Date(data["createdAt"]);
      this.ObjectGroupLinks = this.normalizeObjectGroupLinks(data["objectGroupLinks"]);
      this.ContainerLinks = this.normalizeContainerLinks(data["containerLinks"]);
    }

  }

  static fromJS(data: any): UserViewModel {
    data = typeof data === 'object' ? data : {};
    let result = new UserViewModel();
    result.init(data);
    return result;

  }

  public CanDo(acl: AccessRights): boolean {
    if (acl == AccessRights.EDIT_TRACKERS_IMEI) {
      if ((this.Acl & (1 << AccessRights.EDIT_TRACKERS)) == 0)
        return false;
    }
    return (this.Acl & (1 << acl)) != 0;
  }

  public InterfaceIsPermitted(acl: InterfacePermissions): boolean {
    if (acl < 0) {
      switch (acl) {
        case InterfacePermissions.DATAINFO:
        case InterfacePermissions.DIAGNOSTICS:
          return this.CanDo(AccessRights.VIEW_TRACKER_PACKET);
        case InterfacePermissions.USERS:
          return this.CanDo(AccessRights.EDITUSERS) || this.CanDo(AccessRights.EDIT_ALL_USERS);
      }
      return this.CanDo(AccessRights.EDIT);
    }


    if ((this.InterfacePermissions & (1 << acl)) == 0)
      return false;

    switch (acl) {
      case InterfacePermissions.ANALYTICS:
      case InterfacePermissions.HISTORY:
      case InterfacePermissions.REPORTS:
        return this.CanDo(AccessRights.VIEW_HISTORY);
      case InterfacePermissions.DRIVERS:
      case InterfacePermissions.SERVICE:
        return this.CanDo(AccessRights.VIEW_HISTORY);
    }
    return true;
  }

  public RoleDisplayName(): string {
    if (this.CanDo(AccessRights.EDIT_ALL_USERS))
      return "Administrator";
    if (this.CanDo(AccessRights.EDITUSERS))
      return "Integrator";
    if (this.CanDo(AccessRights.EDIT))
      return "User";

    if (this.CanDo(AccessRights.VIEW_HISTORY))
      return "Demonstration";

    return "Observation";
  }
  public HasFlag(str: string): boolean {
    return this.Flags.indexOf(str) >= 0;
  }
  public SetFlag(str: string) {
    if (!this.HasFlag(str))
      this.Flags = (this.Flags + " " + str).trim();
  }
  public ResetFlag(str: string) {
    this.Flags = this.Flags.replace(str, "").replace("  ", " ").replace("  ", " ").trim();
  }
}

export class UserCreateViewModel extends UserViewModel {
  Password: string = "";
  PasswordConfirmation: string = "";
}
export class UserUpdateViewModel extends UserCreateViewModel {
}

export class NewPasswordViewModel {
  UserId: number;
  Password: string = "";
  PasswordConfirmation: string = "";
}

@Injectable()
export class UsersService implements OnInit {

  private client = inject(HttpClient);
  private syncService = inject(SyncService);
  private currentUser: UserViewModel;
  private title = "Title";
  private userName = "";
  private subUserId: number = 0;
  private settingskeys = {};
  private profileStoreService = inject(ProfileStoreService);

  public onInit = new EventEmitter<UserViewModel>();

  public initializeUser() {
    this.client.get("/ctrl/ApplicationUser/profile").pipe(map(res => {

      this.title = res["title"];
      this.userName = res["userName"];
      this.subUserId = res["subUserId"];

      this.settingskeys = {};

      for (let i in res["settings"])
        this.settingskeys[i] = res["settings"][i];

      return UserViewModel.fromJS(res["user"]);
    })).subscribe((res) => {
      this.currentUser = res;
      this.onInit.emit(res);
    });
  }
  public getUsers(): Observable<ApplicationUserStatisticsView[]> {
    return this.client.get<ApplicationUserStatisticsView[]>("/ctrl/ApplicationUser").pipe(map(values => {
      let result = [];
      if (values && values.constructor === Array) {
        for (let item of values) {
          let user = ApplicationUserStatisticsView.fromJS(item);
          //user.CreatedAt = this.localizationService.toUserTimeZoneDate(user.CreatedAt, this.getCurrentUser());
          result.push(user);
        }
      }

      return result;
    }));
  }

  public getUsersStatistics(): Observable<ApplicationUserStatisticsView[]> {
    return this.client.get<ApplicationUserStatisticsView[]>("/ctrl/ApplicationUser/get-statistics").pipe(map(values => {
      let result = [];
      if (values && values.constructor === Array) {
        for (let item of values)
          result.push(ApplicationUserStatisticsView.fromJS(item))
      }

      return result;
    }));
  }

  public getUsersForGroup(groupId: number): Observable<ApplicationUserStatisticsView[]> {
    return this.client.get<ApplicationUserStatisticsView[]>(`/ctrl/ApplicationUser/get-group-users/${groupId}`).pipe(map(values => {
      let result = [];
      if (values && values.constructor === Array) {
        for (let item of values)
          result.push(ApplicationUserStatisticsView.fromJS(item))
      }

      return result;
    }));
  }

  public getGroupsForUser(userId: number): Observable<ObjectGroupViewModel[]> {
    return this.client.get<ObjectGroupViewModel[]>(`/ctrl/ApplicationUser/get-user-groups/${userId}`).pipe(map(values => {
      let result = [];
      if (values && values.constructor === Array) {
        for (let item of values)
          result.push(ObjectGroupViewModel.fromJS(item))
      }

      return result;
    }));
  }

  public getUsersForContainer(containerId: number): Observable<ApplicationUserStatisticsView[]> {
    return this.client.get<ApplicationUserStatisticsView[]>(`/ctrl/ApplicationUser/get-container-users/${containerId}`).pipe(map(values => {
      const result = [];
      if (values && values.constructor === Array) {
        for (const item of values)
          result.push(ApplicationUserStatisticsView.fromJS(item));
      }

      return result;
    }));
  }

  public getUsersForNewContainer(companyId: number): Observable<ApplicationUserStatisticsView[]> {
    return this.client.get<ApplicationUserStatisticsView[]>(`/ctrl/ApplicationUser/get-new-container-users/${companyId}`).pipe(map(values => {
      const result = [];
      if (values && values.constructor === Array) {
        for (const item of values)
          result.push(ApplicationUserStatisticsView.fromJS(item));
      }

      return result;
    }));
  }
  public getUsersForNewGroup(companyId: number): Observable<ApplicationUserStatisticsView[]> {
    return this.client.get<ApplicationUserStatisticsView[]>(`/ctrl/ApplicationUser/get-new-group-users/${companyId}`).pipe(map(values => {
      const result = [];
      if (values && values.constructor === Array) {
        for (const item of values)
          result.push(ApplicationUserStatisticsView.fromJS(item));
      }

      return result;
    }));
  }

  public getContainersForUser(userId: number): Observable<ContainerViewModel[]> {
    return this.client.get<ContainerViewModel[]>(`/ctrl/ApplicationUser/get-user-containers/${userId}`).pipe(map(values => {
      const result = [];
      if (values && values.constructor === Array) {
        for (const item of values)
          result.push(ContainerViewModel.fromJS(item));
      }

      return result;
    }));
  }

  public getCurrentUser() {
    return this.currentUser;
  }

  public getTitle() {
    return this.title;
  }

  public getUserName() {
    return this.userName;
  }

  public getProfileUserName() {
    return this.subUserId ? `${this.userName} (${this.currentUser.Name})` : this.userName;
  }

  public IsPermitted(bitNumber: AccessRights): boolean {
    let user = this.getCurrentUser();
    if (bitNumber == AccessRights.EDIT_TRACKERS_IMEI) {
      if ((user.Acl & (1 << AccessRights.EDIT_TRACKERS)) == 0)
        return false;
    }
    return user && (user.Acl & (1 << bitNumber)) != 0;
  }

  public InterfaceIsPermitted(bitNumber: InterfacePermissions): boolean {
    let user = this.getCurrentUser();
    return user && user.InterfaceIsPermitted(bitNumber);
  }

  public Settings(name: string, defValue: any): any {
    if (this.settingskeys[name] == 'true')
      return true;
    if (this.settingskeys[name] == 'false')
      return false;
    if (this.settingskeys[name] != null)
      return this.settingskeys[name];
    return defValue;
  }

  public deleteUser(id: number) {
    return this.client.delete("/ctrl/ApplicationUser/" + id)
  }

  public createUser(user: UserCreateViewModel) {
    return this.client.post("/ctrl/ApplicationUser", user).pipe(map(res => {
      return UserViewModel.fromJS(res);
    }));
  }

  public updateUser(user: UserViewModel) {
    return this.client.put("/ctrl/ApplicationUser", user).pipe(map(res => {
      return UserViewModel.fromJS(res);
    }));
  }

  public updateNewUser(user: UserUpdateViewModel) {
    return this.client.put("/ctrl/ApplicationUser", user).pipe(map(res => {
      return UserViewModel.fromJS(res);
    }));
  }

  public userSelected(id: number) {
    return this.client.get(`/ctrl/ApplicationUser/update-subuser-id/${id}`).pipe((res) => {
      this.profileStoreService.clearSelectedObjectId();
      return res;
    });
  }

  public subuserLogout() {
    return this.userSelected(0);
  }

  public logout() {
    const content_ = JSON.stringify({ returnUrl: "/" });
    const options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        "Accept": "application/json"
      })
    };
    this.client.request("post", "/Identity/Logout", options_).subscribe(
      () => {
        location.reload();
        localStorage.clear();
      },
      response => {
        console.log(response);
      });
  }

  changePassword(changePasswordModel: ChangePasswordViewModel) {
    return this.client.post("/ctrl/ApplicationUser/SelfChangePassword", changePasswordModel);
  }

  public getUserDetails(id: number) {
    return this.client.get("/ctrl/ApplicationUser/" + id).pipe(map(res => {
      return UserViewModel.fromJS(res);
    }));
  }

  changeUserPassword(input: NewPasswordViewModel) {
    return this.client.post("/ctrl/ApplicationUser/ChangePassword", input);
  }

  ngOnInit(): void {

  }
}
